// SubAgentActivityItem — nested cognitive/sub-agent activity, collapsed by default.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAtom } from 'jotai';
import { focusedActivityNodeAtom } from './running-work-focus';
import type { SubAgentFeed } from './activity-feed';
import { useOpenParticipantTab } from './participant-tabs-context';

export interface SubAgentActivityItemProps {
  feed: SubAgentFeed;
  children: ReactNode;
}

const PARTICIPANT_STATUS_LABELS: Record<NonNullable<SubAgentFeed['participantStatus']>, string> = {
  running: 'Running',
  idle: 'Idle',
  settled: 'Done',
  failed: 'Failed',
  stopped: 'Stopped',
};

function statusLabel(feed: SubAgentFeed): string {
  if (feed.participantStatus) return PARTICIPANT_STATUS_LABELS[feed.participantStatus];
  if (feed.status === 'running') return 'Running';
  if (feed.status === 'failed') return 'Failed';
  if (feed.status === 'stopped') return 'Stopped';
  return 'Done';
}

function statusClass(feed: SubAgentFeed): string {
  if (feed.participantStatus === 'idle') return 'text-muted-foreground';
  if (feed.status === 'running') return 'text-info-text';
  if (feed.status === 'failed') return 'text-error-text';
  if (feed.status === 'stopped') return 'text-muted-foreground';
  return 'text-success-text';
}

export function SubAgentActivityItem({ feed, children }: SubAgentActivityItemProps) {
  const [open, setOpen] = useState(false);
  const openParticipantTab = useOpenParticipantTab();
  const participantId = feed.participantId;
  const [focused, setFocused] = useAtom(focusedActivityNodeAtom);
  const rowRef = useRef<HTMLDivElement>(null);
  const [highlighted, setHighlighted] = useState(false);
  useEffect(() => {
    if (!focused || focused.nodeId !== feed.nodeId) return;
    rowRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlighted(true);
    const at = focused.at;
    const timer = setTimeout(() => {
      setHighlighted(false);
      setFocused((current) => (current?.at === at ? null : current));
    }, 3000);
    return () => clearTimeout(timer);
  }, [focused, feed.nodeId, setFocused]);

  return (
    <div
      ref={rowRef}
      data-testid="sub-agent-activity"
      data-node-id={feed.nodeId}
      data-focused={highlighted ? 'true' : undefined}
      data-expanded={open}
      data-participant-id={participantId}
      data-background={feed.background === true ? 'true' : undefined}
      data-participant-status={feed.participantStatus}
      className={`mt-2 border-l-2 pl-3 transition-colors ${highlighted ? 'border-primary bg-primary/10' : 'border-border/70'}`}
    >
      <div className="flex items-baseline gap-2">
      <button
        type="button"
        data-testid="sub-agent-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-baseline gap-2 py-1 text-left text-body hover:text-foreground"
      >
        <span
          className={`inline-block shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
          aria-hidden
        >
          ▸
        </span>
        <span className="font-medium text-foreground">{feed.label}</span>
        <span data-testid="sub-agent-status" className={`text-caption ${statusClass(feed)}`}>{statusLabel(feed)}</span>
        {feed.fanout && feed.status === 'running' && feed.fanout.done < feed.fanout.total ? (
          <span data-testid="sub-agent-fanout" className="text-caption text-muted-foreground">
            Waiting on {feed.fanout.total - feed.fanout.done} of {feed.fanout.total} subagents
          </span>
        ) : null}
        {feed.summary && !open && (
          <span className="min-w-0 flex-1 truncate text-caption text-muted-foreground">{feed.summary}</span>
        )}
      </button>
      {participantId && openParticipantTab ? (
        <button
          type="button"
          data-testid="sub-agent-open-tab"
          onClick={() => openParticipantTab(participantId)}
          className="shrink-0 rounded-md px-1.5 py-0.5 text-caption text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          Open
        </button>
      ) : null}
      </div>
      {open && (
        <div data-testid="sub-agent-body" className="space-y-1 pb-1">
          {children}
        </div>
      )}
    </div>
  );
}
